"use client"

import { useState } from "react"
import { Pressable } from "react-native"
import clsx from "clsx"
import { styled } from "nativewind"
import { FormattedMessage } from "react-intl"

import {
  Box,
  Button,
  getCategoryEmoji,
  Icon,
  P,
  Sheet,
  useCategories,
  useUserContext,
} from "@bullseye/components"
import { Category } from "@bullseye/types"

const NEW_MEMBER_CATEGORY_ID = "new-member"
const MAX_CATEGORIES = 2

const ChipButton = styled(
  Pressable,
  "flex flex-row items-center rounded-full border border-solid px-3 py-1.5 g-1.5",
)

type CategoryChipProps = {
  category: Category
  selected: boolean
  onToggle: (id: string) => void
  disabled: boolean
}

const CategoryChip = ({
  category,
  selected,
  onToggle,
  disabled,
}: CategoryChipProps) => {
  return (
    <ChipButton
      className={clsx("px-4 py-2", {
        "border-primary-500 bg-primary-50": selected,
        "border-gray-50 bg-gray-50": !selected,
        "opacity-50": disabled && !selected,
      })}
      onPress={() => onToggle(category.id)}
      disabled={disabled && !selected}
    >
      {getCategoryEmoji(category.title) !== "" && (
        <P>{getCategoryEmoji(category.title)}</P>
      )}
      <P>{category.title}</P>
    </ChipButton>
  )
}

type Props = {
  value: string[]
  onChange: (ids: string[]) => void
  disabled?: boolean
}

export const CategoryPicker = ({ value = [], onChange, disabled }: Props) => {
  const [isOpen, setIsOpen] = useState(false)
  const [draft, setDraft] = useState<string[]>(value)
  const { user, accessToken } = useUserContext()
  const { categories, isLoading } = useCategories({ accessToken })

  const visibleCategories = categories.filter((c) => {
    if (c.id === NEW_MEMBER_CATEGORY_ID && !user.admin) return false
    return true
  })

  const selectedCategories = visibleCategories.filter((c) =>
    value.includes(c.id),
  )

  function handleOpen() {
    if (disabled) return
    setDraft(value)
    setIsOpen(true)
  }

  function handleToggle(id: string) {
    setDraft((prev) => {
      if (prev.includes(id)) {
        return prev.filter((v) => v !== id)
      }
      if (prev.length >= MAX_CATEGORIES) return prev
      return [...prev, id]
    })
  }

  function handleSave() {
    onChange(draft)
    setIsOpen(false)
  }

  function handleClear() {
    setDraft([])
  }

  const hasSelection = selectedCategories.length > 0

  function handleRemove(id: string) {
    onChange(value.filter((v) => v !== id))
  }

  return (
    <>
      <Box className="flex g-2">
        <ChipButton
          className={clsx(
            "min-h-14 justify-between rounded-lg border-gray-100 bg-white px-2.5",
            {
              "opacity-50": disabled,
              "py-2": hasSelection,
            },
          )}
          onPress={handleOpen}
          disabled={disabled}
          role="button"
        >
          <Box className="flex flex-1 g-1">
            <P size="sm" className="text-gray-500">
              <FormattedMessage id="activityForm.category.label" />
            </P>
            {hasSelection ? (
              <Box className="flex flex-row flex-wrap g-2">
                {selectedCategories.map((c) => (
                  <ChipButton
                    key={c.id}
                    onPress={() => handleRemove(c.id)}
                    disabled={disabled}
                    className="border-gray-50 bg-gray-50 px-3 py-1"
                  >
                    {getCategoryEmoji(c.title) !== "" && (
                      <P size="sm">{getCategoryEmoji(c.title)}</P>
                    )}
                    <P size="sm">{c.title}</P>
                    <Icon type="x" size="sm" color="text-gray-950" />
                  </ChipButton>
                ))}
              </Box>
            ) : (
              <P className="text-gray-500">
                <FormattedMessage id="activityForm.category.placeholder" />
              </P>
            )}
          </Box>
          <Icon
            type="chevron-down"
            size="sm"
            color={disabled ? "text-gray-300" : "text-gray-950"}
          />
        </ChipButton>

        {value.length === 0 && (
          <Box className="flex flex-row items-center g-2">
            <Icon type="info-circle" size="sm" color="text-gray-950" />
            <P className="flex-1 text-gray-950">
              <FormattedMessage id="activityForm.category.hint" />
            </P>
          </Box>
        )}
      </Box>

      <Sheet
        show={isOpen}
        dismiss={() => setIsOpen(false)}
        icon="x"
        size="md"
        renderContent={() => (
          <Box className="flex g-4">
            <Box className="flex g-1">
              <P bold className="text-lg">
                <FormattedMessage id="activityForm.category.popupTitle" />
              </P>
              <P className="text-gray-500">
                <FormattedMessage id="activityForm.category.popupSubtitle" />
              </P>
            </Box>
            {!isLoading && (
              <Box className="flex flex-row flex-wrap g-2">
                {visibleCategories.map((category) => (
                  <CategoryChip
                    key={category.id}
                    category={category}
                    selected={draft.includes(category.id)}
                    onToggle={handleToggle}
                    disabled={
                      draft.length >= MAX_CATEGORIES &&
                      !draft.includes(category.id)
                    }
                  />
                ))}
              </Box>
            )}
            <Box className="flex flex-row g-2">
              <Button
                as="button"
                handlePress={handleClear}
                variant="tertiary"
                className="flex-1"
                disabled={draft.length === 0}
              >
                <FormattedMessage id="activityForm.category.clearAll" />
              </Button>
              <Button
                as="button"
                handlePress={handleSave}
                className="flex-1"
              >
                <FormattedMessage id="activityForm.category.save" />
              </Button>
            </Box>
          </Box>
        )}
      />
    </>
  )
}
